import packsIcon from "@loot-os/icons/icons/packs";
import { Badge } from "../../core/Badge.js";
import { buttonStyles } from "../../core/Button.styles.js";
import { cardStyles } from "../../core/Card.styles.js";
import { Text } from "../../core/Text.js";
import { AppLink } from "../../foundations/AppLink.js";
import { cn } from "../../foundations/cn.js";
import { STRETCHED_LINK } from "../../foundations/interaction.js";
import { Amount } from "../Amount.js";
import { BoxArt } from "../BoxArt.js";
import { BattleRoundsStrip } from "./BattleRoundsStrip.js";
import { BattleSeat } from "./BattleSeat.js";
import type { BattleRoundBox, BattleSeatView } from "./battleTypes.js";

/** A word on the card's head: the mode, Private, Fast spin. */
export interface BattleCardTag {
  label: string;
  /** Read on hover and by a screen reader: what the tag means ("Lowest total value wins"). */
  hint?: string;
  tone?: "neutral" | "accent" | "danger";
}

export interface BattleCardProps {
  /** The match-up: "1v1", "1v1v1". */
  title: string;
  tags: readonly BattleCardTag[];
  /** What every seat pays, in cents. */
  priceCents: number;
  rounds: readonly BattleRoundBox[];
  /** "4 boxes · 4 rounds" beside the strip. */
  roundsSummary: string;
  /** Set while it runs: "Round 2 of 4". */
  progress?: string;
  seats: readonly BattleSeatView[];
  /** "1/2 seats", for a reader and beside the action. */
  seatsSummary: string;
  /** Where the card goes: Join, Watch, Results. */
  action: { label: string; href: string; emphasis: "primary" | "secondary" };
  /** A finished battle fades behind the live ones. */
  finished?: boolean;
  /** The round being opened, 0-based, raised in the row's strip; left out before and after. */
  currentRound?: number;
  /** How many seats make a team, so the row marks VS between teams; 1 (every seat its own). */
  teamSize?: number;
  roundLabel?: (index: number, name: string) => string;
  /**
   * `card`: a card for a grid. `row`: one wide line for a list down the page, the match-up and its
   * seats, then the boxes, then the price and the way in; phones stack it like a card.
   */
  layout?: "card" | "row";
  className?: string;
}

/**
 * One battle in the lobby: the match-up, its mode and price, the boxes in
 * round order, who is in (an open seat dashed) and the one way in.
 */
export function BattleCard({
  title,
  tags,
  priceCents,
  rounds,
  roundsSummary,
  progress,
  seats,
  seatsSummary,
  action,
  finished = false,
  currentRound,
  teamSize = 1,
  roundLabel,
  layout = "card",
  className,
}: BattleCardProps) {
  if (layout === "row") {
    return (
      <BattleRow
        title={title}
        tags={tags}
        priceCents={priceCents}
        rounds={rounds}
        roundsSummary={roundsSummary}
        progress={progress}
        seats={seats}
        seatsSummary={seatsSummary}
        action={action}
        finished={finished}
        currentRound={currentRound}
        teamSize={teamSize}
        roundLabel={roundLabel}
        className={className}
      />
    );
  }
  const heading = (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <Text as="h3" variant="display-xl4" tone="primary">
        {title}
      </Text>
      {tags.map((tag) => (
        <span key={tag.label} title={tag.hint}>
          <Badge tone={tag.tone ?? "neutral"}>{tag.label}</Badge>
          {tag.hint && <span className="sr-only">: {tag.hint}</span>}
        </span>
      ))}
    </div>
  );
  const seatList = (
    <ul className="flex min-w-0 flex-wrap gap-2">
      {seats.map((seat, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a seat's place is its identity
        <li key={index} className="min-w-0">
          <BattleSeat seat={seat} layout="stacked" size="md" />
        </li>
      ))}
    </ul>
  );
  const way = (
    <AppLink
      href={action.href}
      className={buttonStyles({
        variant: action.emphasis === "primary" ? "primary" : "secondary",
        size: "sm",
      })}
    >
      {action.label}
    </AppLink>
  );
  return (
    <article
      aria-label={`${title}, ${seatsSummary}`}
      className={cn(
        cardStyles({ radius: "2xl" }),
        "flex min-w-0 flex-col gap-4 p-4",
        // A finished battle steps back by its art alone; its words keep their contrast.
        finished && "[&_img]:opacity-60",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        {heading}
        <Amount cents={priceCents} className="text-text-primary" />
      </div>
      <div className="flex min-w-0 items-center gap-3">
        <BattleRoundsStrip rounds={rounds} max={6} roundLabel={roundLabel} />
        <span className="shrink-0 font-mono text-mono-meta text-text-tertiary">
          {roundsSummary}
        </span>
      </div>
      {progress && (
        <Text variant="body-b3-strong" tone="accent">
          {progress}
        </Text>
      )}
      <div className="mt-auto flex items-end justify-between gap-3">
        {seatList}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="font-body text-body-b3 text-text-tertiary max-sm:hidden">
            {seatsSummary}
          </span>
          {way}
        </div>
      </div>
    </article>
  );
}

/** The seats in teams, each team's avatars tucked together, VS between two teams. */
function RowSeats({ seats, teamSize }: { seats: readonly BattleSeatView[]; teamSize: number }) {
  const size = Math.max(1, teamSize);
  const teams = Array.from({ length: Math.ceil(seats.length / size) }, (_, i) =>
    seats.slice(i * size, i * size + size),
  );
  return (
    <ul className="relative z-1 flex min-w-0 shrink-0 items-center gap-1">
      {teams.map((team, t) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a team's place is its identity
        <li key={t} className="flex items-center gap-1">
          {t > 0 && (
            <span aria-hidden className="font-mono text-mono-meta text-text-secondary uppercase">
              vs
            </span>
          )}
          <span className="flex items-center -space-x-1.5">
            {team.map((seat, i) => (
              <BattleSeat
                // biome-ignore lint/suspicious/noArrayIndexKey: a seat's place is its identity
                key={i}
                seat={seat}
                layout="avatar"
                size="sm"
                className="rounded-pill ring-2 ring-bg-surface"
              />
            ))}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The row: the match-up and its seats, a sunken strip of the boxes by round that scrolls sideways
 * with the rounds in its corner, then the price and the way in, a fixed slot each so a list's rows
 * line up. The whole row follows the way in; the seats and the strip stay targets of their own.
 * Phones stack it, the price and the way in on a see-through card at its foot.
 */
function BattleRow({
  title,
  tags,
  priceCents,
  rounds,
  roundsSummary,
  progress,
  seats,
  seatsSummary,
  action,
  finished,
  currentRound,
  teamSize,
  roundLabel = (index, name) => `Round ${index + 1}: ${name}`,
  className,
}: Omit<BattleCardProps, "layout"> & { finished: boolean; teamSize: number }) {
  return (
    <article
      aria-label={`${title}, ${seatsSummary}`}
      className={cn(
        // The action stretches over the row, so the whole row raises under the pointer.
        cardStyles({ radius: "2xl", hover: "raise" }),
        "relative grid min-w-0 gap-3 p-4",
        "lg:grid-cols-[16rem_minmax(0,1fr)_auto] lg:items-center lg:gap-6 lg:py-2",
        finished && "[&_img]:opacity-60",
        className,
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-2 lg:flex-col lg:justify-center lg:gap-3">
        <h3 className="flex min-w-0 flex-wrap items-center gap-1.5 font-body text-body-b2-strong text-text-primary uppercase">
          {title}
          {tags.map((tag) => (
            <span key={tag.label} title={tag.hint} className="normal-case">
              <Badge tone={tag.tone ?? "neutral"}>{tag.label}</Badge>
              {tag.hint && <span className="sr-only">: {tag.hint}</span>}
            </span>
          ))}
        </h3>
        <RowSeats seats={seats} teamSize={teamSize} />
      </div>
      <div className="relative z-1 min-w-0 overflow-hidden rounded-lg bg-bg-inset">
        <ol
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a strip that scrolls sideways must be reachable by keyboard (WCAG 2.1.1).
          tabIndex={0}
          aria-label={roundsSummary}
          className="flex gap-2 overflow-x-auto overscroll-x-contain p-2 pe-24 outline-hidden [scrollbar-width:none] focus-visible:focus-ring [&::-webkit-scrollbar]:hidden"
        >
          {rounds.map((round, index) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: rounds repeat boxes; the round is the key
              key={index}
              aria-current={index === currentRound ? "step" : undefined}
              className="flex h-18 w-14 shrink-0 items-center justify-center rounded-lg aria-[current=step]:bg-bg-raised"
            >
              <BoxArt
                src={round.imageUrl}
                alt={roundLabel(index, round.name)}
                className="h-16 w-auto max-w-full motion-safe:transition-transform motion-safe:duration-fast motion-safe:ease-standard motion-safe:hover:-translate-y-0.5"
              />
            </li>
          ))}
        </ol>
        {/* The rounds as a count ("4", or "3/4" while it runs); readers hear the words. */}
        <span aria-hidden className="pointer-events-none absolute end-2 top-2">
          <Badge icon={packsIcon}>
            {currentRound === undefined ? rounds.length : `${currentRound + 1}/${rounds.length}`}
          </Badge>
        </span>
        {progress && <span className="sr-only">{progress}</span>}
      </div>
      <div className="flex items-center gap-4 rounded-xl bg-bg-inset/60 p-2 lg:justify-self-end lg:gap-6 lg:bg-transparent lg:p-0">
        {/* The seats summary is in the article's name; the seats themselves show who is in. */}
        <span className="flex flex-1 justify-center lg:min-w-30 lg:flex-none lg:justify-end">
          <Amount cents={priceCents} size="lg" className="text-text-primary" />
        </span>
        <AppLink
          href={action.href}
          className={buttonStyles({
            variant: action.emphasis === "primary" ? "primary" : "secondary",
            size: "md",
            className: cn("w-40 shrink-0 max-[22.5rem]:w-32", STRETCHED_LINK),
          })}
        >
          {action.label}
        </AppLink>
      </div>
    </article>
  );
}
